import React, { useState, useEffect, useRef, useCallback } from "react";
import fetchData from "../api/fetchData";

/* ─── Tiny inline Toast component ──────────────────────────── */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bg =
    type === "error"
      ? "bg-red-600"
      : type === "warning"
      ? "bg-amber-500"
      : "bg-emerald-600";

  return (
    <div
      className={`fixed top-6 right-6 z-[100] ${bg} text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-slide-in max-w-md`}
    >
      <span className="text-lg">
        {type === "error" ? "❌" : type === "warning" ? "⚠️" : "✅"}
      </span>
      <p className="text-sm font-medium flex-1">{message}</p>
      <button onClick={onClose} className="text-white/80 hover:text-white text-xl leading-none">
        ×
      </button>
    </div>
  );
};

/* ─── Main Page ────────────────────────────────────────────── */
const StoresPage = () => {
  const [missingLogos, setMissingLogos] = useState([]);
  const [missingAffiliateLinks, setMissingAffiliateLinks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Logo validation state
  const [logoFixLoading, setLogoFixLoading] = useState(false);
  const [logoFixResult, setLogoFixResult] = useState(null);
  const [logoFixError, setLogoFixError] = useState("");
  const [progress, setProgress] = useState(null); // live progress from polling

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "error") => setToast({ message, type });

  // Polling ref
  const pollRef = useRef(null);

  /* ── Poll /logo-validation-status every 2s ─────────────── */
  const startPolling = useCallback(() => {
    if (pollRef.current) return; // already polling
    pollRef.current = setInterval(async () => {
      try {
        const status = await fetchData("/stores/logo-validation-status");
        setProgress(status);

        if (!status.running) {
          // Process finished
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLogoFixLoading(false);
          setLogoFixResult({
            message: "Logo validation completed!",
            totalProcessed: status.processed,
            totalUpdated: status.updated,
            totalFailed: status.failed,
          });
          showToast(
            `Done! Updated ${status.updated} logos, ${status.failed} failed.`,
            "success"
          );
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 2000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  /* ── On mount: check if validation is already running ──── */
  useEffect(() => {
    const checkInitialStatus = async () => {
      try {
        const status = await fetchData("/stores/logo-validation-status");
        if (status.running) {
          setLogoFixLoading(true);
          setProgress(status);
          startPolling();
          showToast("A logo validation process is already running. Tracking progress...", "warning");
        }
      } catch (err) {
        // Backend might be down, ignore
      }
    };
    checkInitialStatus();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  /* ── Validate stores (check missing items) ─────────────── */
  const handleCheckMissingItems = async () => {
    setLoading(true);
    setMissingLogos([]);
    setMissingAffiliateLinks([]);

    try {
      const data = await fetchData("/stores/all");

      const storesWithoutLogo = data
        .filter((store) => !store.brandLogoUrlSquare)
        .map((store) => store.name);

      const storesWithoutAffiliateLink = data
        .filter((store) => !store.affiliateLink)
        .map((store) => store.name);

      setMissingLogos(storesWithoutLogo);
      setMissingAffiliateLinks(storesWithoutAffiliateLink);
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("Failed to fetch store data.", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ── Fix missing logos ─────────────────────────────────── */
  const handleValidateMissingLogos = async () => {
    // If already running, block with toast
    if (logoFixLoading) {
      showToast("Logo validation is already in progress! Please wait.", "warning");
      return;
    }

    setLogoFixLoading(true);
    setLogoFixResult(null);
    setLogoFixError("");
    setProgress(null);

    try {
      const data = await fetchData("/stores/validate-missing-logos", null, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      // If the API accepted it, start polling
      if (data.message) {
        showToast(data.message, "success");
      }
      startPolling();
    } catch (error) {
      console.error("Error validating missing logos:", error);
      // Check if it's a 409 (already running)
      const errMsg = error.message || "";
      if (errMsg.includes("409") || errMsg.toLowerCase().includes("already running")) {
        showToast("⚠️ Logo validation is already running on the server!", "warning");
        setLogoFixLoading(true);
        startPolling();
      } else {
        setLogoFixError(errMsg || "Failed to validate missing logos.");
        setLogoFixLoading(false);
        showToast("Failed to start logo validation.", "error");
      }
    }
  };

  /* ── Progress bar helper ───────────────────────────────── */
  const progressPercent =
    progress && progress.totalToProcess > 0
      ? Math.round((progress.processed / progress.totalToProcess) * 100)
      : 0;

  return (
    <div className="flex-1 p-4 md:p-6 bg-gray-100 overflow-y-auto">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-6 md:mb-8 text-center">
        Store Data Validation
      </h1>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-6 md:mb-8">
        <button
          onClick={handleCheckMissingItems}
          disabled={loading}
          className="px-6 py-3 md:px-8 md:py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-lg md:text-xl font-semibold rounded-full shadow-lg hover:from-blue-600 hover:to-indigo-600 transition duration-300 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Checking..." : "Validate Stores"}
        </button>

        <button
          onClick={handleValidateMissingLogos}
          disabled={logoFixLoading}
          className={`px-6 py-3 md:px-8 md:py-4 text-white text-lg md:text-xl font-semibold rounded-full shadow-lg transition duration-300 ease-in-out flex items-center gap-2 ${
            logoFixLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          }`}
        >
          {logoFixLoading && (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {logoFixLoading ? "Fixing Logos..." : "🔧 Fix Missing Logos"}
        </button>
      </div>

      {/* ── Live Progress Card ──────────────────────────────── */}
      {logoFixLoading && progress && progress.totalToProcess > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-lg mb-6 border-l-4 border-blue-500 animate-fade-in">
          <h2 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Logo Validation In Progress...
          </h2>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-emerald-500 h-4 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 mb-4 text-center font-medium">
            {progressPercent}% — {progress.processed} / {progress.totalToProcess} processed
          </p>

          {/* Stat cards */}
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-center min-w-[100px]">
              <span className="text-xs text-blue-600 font-medium">Total</span>
              <p className="text-2xl font-bold text-blue-800">{progress.totalToProcess}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-center min-w-[100px]">
              <span className="text-xs text-amber-600 font-medium">Processed</span>
              <p className="text-2xl font-bold text-amber-800">{progress.processed}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 text-center min-w-[100px]">
              <span className="text-xs text-emerald-600 font-medium">Updated</span>
              <p className="text-2xl font-bold text-emerald-800">{progress.updated}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-center min-w-[100px]">
              <span className="text-xs text-red-600 font-medium">Failed</span>
              <p className="text-2xl font-bold text-red-800">{progress.failed}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 text-center min-w-[100px]">
              <span className="text-xs text-purple-600 font-medium">Remaining</span>
              <p className="text-2xl font-bold text-purple-800">{progress.remaining}</p>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen loading overlay (only for validate stores, not logo fix) */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 backdrop-blur-sm z-50">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4">
            <div className="w-16 h-16 border-4 border-red-600 border-solid rounded-full animate-spin border-t-transparent" />
            <p className="text-gray-700 font-medium text-lg">Validating stores...</p>
          </div>
        </div>
      )}

      {/* Logo Fix Result (after completion) */}
      {logoFixResult && !logoFixLoading && (
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg mb-6 border-l-4 border-emerald-500">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-emerald-500">✅</span> Logo Validation Complete
          </h2>

          {typeof logoFixResult === "string" && (
            <p className="text-lg text-gray-700">{logoFixResult}</p>
          )}

          {typeof logoFixResult === "object" && (
            <div className="space-y-3">
              {logoFixResult.message && (
                <p className="text-lg text-gray-700 font-medium">{logoFixResult.message}</p>
              )}
              {logoFixResult.totalProcessed !== undefined && (
                <div className="flex flex-wrap gap-4 mt-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                    <span className="text-sm text-blue-600 font-medium">Total Processed</span>
                    <p className="text-2xl font-bold text-blue-800">{logoFixResult.totalProcessed}</p>
                  </div>
                  {logoFixResult.totalUpdated !== undefined && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                      <span className="text-sm text-emerald-600 font-medium">Updated</span>
                      <p className="text-2xl font-bold text-emerald-800">{logoFixResult.totalUpdated}</p>
                    </div>
                  )}
                  {logoFixResult.totalFailed !== undefined && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                      <span className="text-sm text-red-600 font-medium">Failed</span>
                      <p className="text-2xl font-bold text-red-800">{logoFixResult.totalFailed}</p>
                    </div>
                  )}
                </div>
              )}

              {!logoFixResult.message && logoFixResult.totalProcessed === undefined && (
                <pre className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 overflow-x-auto">
                  {JSON.stringify(logoFixResult, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Logo Fix Error */}
      {logoFixError && (
        <div className="bg-red-50 border border-red-200 p-6 rounded-lg shadow-md mb-6 border-l-4 border-l-red-500">
          <h2 className="text-xl font-semibold text-red-800 mb-2 flex items-center gap-2">
            <span>❌</span> Logo Validation Failed
          </h2>
          <p className="text-red-700">{logoFixError}</p>
        </div>
      )}

      {/* Existing Validation Results */}
      {(missingLogos.length > 0 || missingAffiliateLinks.length > 0) && (
        <div className="bg-white p-8 rounded-lg shadow-lg max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {missingLogos.length > 0 && (
              <div>
                <h2 className="text-3xl font-semibold text-gray-800 mb-6 text-center">
                  Missing Logos
                </h2>
                <ul className="list-disc list-inside">
                  {missingLogos.map((name) => (
                    <li
                      key={name}
                      className="text-lg text-gray-700 bg-gray-100 p-3 rounded-lg shadow-sm mb-2"
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {missingAffiliateLinks.length > 0 && (
              <div>
                <h2 className="text-3xl font-semibold text-gray-800 mb-6 text-center">
                  Missing Affiliate Links
                </h2>
                <ul className="list-disc list-inside">
                  {missingAffiliateLinks.map((name) => (
                    <li
                      key={name}
                      className="text-lg text-gray-700 bg-gray-100 p-3 rounded-lg shadow-sm mb-2"
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {missingLogos.length === 0 &&
        missingAffiliateLinks.length === 0 &&
        !loading &&
        !logoFixResult &&
        !logoFixError &&
        !logoFixLoading && (
          <div className="bg-white p-6 rounded-lg shadow-md mt-6 text-center">
            <p className="text-lg text-gray-600">
              All stores have logos and affiliate links.
            </p>
          </div>
        )}

      {/* Inline CSS for animations */}
      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .animate-slide-in {
          animation: slide-in 0.35s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default StoresPage;
